import type {
  ActivityHistoryItem,
  ActivityHistoryState,
  ActivityHistoryValue,
  ValueRow,
  ResultItem,
} from "./types";
import {
  AttributeSchemaBase,
  ProductListPriceSchema,
  AttributeObjectType,
} from "../../../../types/types.PIM";
import type { UUID } from "../../../../types/types";
import type { TFunction } from "react-i18next";
import { isMultiValue } from "./utils";

const PRICING_FIELDS = [
  { name: "SKU Number", key: "sku_number", inputType: "form_field" },
  { name: "Min. No. of Units", key: "minimum_units", inputType: "numeric" },
  { name: "Min. Order Quantity", key: "quantity", inputType: "numeric" },
  { name: "Price ($/UoM)", key: "price", inputType: "numeric" },
  { name: "Valid Until", key: "valid_until", inputType: "date" },
  { name: "Currency", key: "currency", inputType: "form_field" },
] as const;

export function hasCollectionData(item: ActivityHistoryItem): boolean {
  return (
    Boolean(item.state_before?.value_row) ||
    Boolean(item.state_after?.value_row) ||
    Boolean(item.state_before?.value_rows?.length) ||
    Boolean(item.state_after?.value_rows?.length)
  );
}

function hasPricingData(item: ActivityHistoryItem): boolean {
  return (
    Boolean(item.state_before?.list_price) ||
    Boolean(item.state_after?.list_price) ||
    Boolean(item.state_before?.list_prices?.length) ||
    Boolean(item.state_after?.list_prices?.length)
  );
}

function getRowsFromState(state: ActivityHistoryState): ValueRow[] {
  if (state.value_rows && state.value_rows.length > 0) {
    return state.value_rows;
  }
  if (state.value_row) {
    return [state.value_row];
  }
  return [];
}

function getPricesFromState(
  state: ActivityHistoryState
): ProductListPriceSchema[] {
  if (state.list_prices && state.list_prices.length > 0) {
    return state.list_prices;
  }
  if (state.list_price) {
    return [state.list_price];
  }
  return [];
}

function convertPriceToValueRow(price: ProductListPriceSchema): ValueRow {
  const values: ActivityHistoryValue[] = PRICING_FIELDS.map((field) => {
    const fieldKey = field.key;
    const value = price[fieldKey as keyof ProductListPriceSchema];

    return {
      attribute_id: fieldKey,
      data_type:
        fieldKey === "valid_until"
          ? "date"
          : typeof value === "number"
          ? "numeric"
          : "text",
      id: `${price.id}-${fieldKey}`,
      priority: 0,
      value: value as string | number | boolean | undefined,
    };
  }).filter((v) => v.value !== undefined && v.value !== null);

  return {
    collection_id: price.id,
    row_id: price.id,
    values,
  };
}

function getPricingRowsFromState(state: ActivityHistoryState): ValueRow[] {
  const prices = getPricesFromState(state);
  return prices.map(convertPriceToValueRow);
}

function createPricingAttributes(t: TFunction): AttributeSchemaBase[] {
  return PRICING_FIELDS.map((field) => ({
    id: field.key,
    display_name: t(field.name),
    input_type: field.inputType,
    name: t(field.name),
    description: null,
    is_editable: true,
    is_required: false,
    is_mandatory: false,
    is_system: false,
    is_master: false,
    is_restricted: false,
    is_visible: true,
    is_filterable: false,
    default_values: null,
    values: [],
    list_name: null,
    list_id: "" as UUID,
    object_type: "product" as AttributeObjectType,
    new_object_type: "product",
    translations: [],
    size: "small" as const,
    icons_enabled: false,
  }));
}

function collectAttributes(
  state_before: ActivityHistoryState,
  state_after: ActivityHistoryState
): AttributeSchemaBase[] {
  const attributes: AttributeSchemaBase[] = [];
  const seen = new Set<string>();

  const add = (list?: AttributeSchemaBase[]) => {
    list?.forEach((attr) => {
      if (attr?.id && !seen.has(attr.id)) {
        attributes.push(attr);
        seen.add(attr.id);
      }
    });
  };

  add(state_after.attributes);
  add(state_before.attributes);

  return attributes;
}

function valuesAreDifferent(
  before: ActivityHistoryValue[],
  after: ActivityHistoryValue[]
): boolean {
  if (before.length !== after.length) return true;

  const beforeStr = before
    .map((v) => JSON.stringify(v.value))
    .sort()
    .join("|");
  const afterStr = after
    .map((v) => JSON.stringify(v.value))
    .sort()
    .join("|");

  return beforeStr !== afterStr;
}

function determineAttributeOperation(
  beforeValues: ActivityHistoryValue[],
  afterValues: ActivityHistoryValue[],
  inputType: string
): "added" | "removed" | "updated" | "unchanged" {
  const hasBefore = beforeValues.length > 0;
  const hasAfter = afterValues.length > 0;

  if (!hasBefore && hasAfter) {
    return "added";
  }

  if (hasBefore && !hasAfter) {
    return "removed";
  }

  if (hasBefore && hasAfter) {
    if (inputType === "toggle") {
      const beforeStr = beforeValues[0]?.value ?? false;
      const afterStr = afterValues[0]?.value ?? false;
      if (beforeStr === afterStr) {
        return "unchanged";
      }
    }

    if (valuesAreDifferent(beforeValues, afterValues)) {
      return "updated";
    }
  }

  return "unchanged";
}

function mapActions(action: string): string {
  switch (action) {
    case "added":
      return "Added";
    case "removed":
      return "Removed";
    case "updated":
      return "Updated";
  }
  return action;
}

function buildRowKey(row: ValueRow | undefined, index: number): string {
  return row?.row_id || `__row_${index}`;
}

function collectAttributeValues(
  row: ValueRow | undefined,
  attributeId: string,
  keepAllValues: boolean
): ActivityHistoryValue[] {
  if (!row) return [];
  const matchingValues = row.values.filter(
    (value) => value.attribute_id === attributeId
  );

  if (keepAllValues) {
    return matchingValues.sort((a, b) => {
      const aModified = a.modified_at || "";
      const bModified = b.modified_at || "";
      return bModified.localeCompare(aModified);
    });
  }

  if (matchingValues.length > 1) {
    return [
      matchingValues.sort((a, b) => {
        const aModified = a.modified_at || "";
        const bModified = b.modified_at || "";
        return bModified.localeCompare(aModified);
      })[0],
    ];
  }

  return matchingValues;
}

function valuesAreEqual(
  val1: ActivityHistoryValue,
  val2: ActivityHistoryValue
): boolean {
  if (val1.enum_variant?.id && val2.enum_variant?.id) {
    return val1.enum_variant.id === val2.enum_variant.id;
  }

  if (val1.product_ref?.id && val2.product_ref?.id) {
    return val1.product_ref.id === val2.product_ref.id;
  }

  return val1.value === val2.value;
}

function getChangedAfterValues(
  beforeValues: ActivityHistoryValue[],
  afterValues: ActivityHistoryValue[]
): ActivityHistoryValue[] {
  if (beforeValues.length === 0) {
    return afterValues;
  }

  if (afterValues.length === 1) {
    return afterValues;
  }

  // Filter out values that exist in before
  const changedValues = afterValues.filter((afterVal) => {
    return !beforeValues.some((beforeVal) =>
      valuesAreEqual(beforeVal, afterVal)
    );
  });

  return changedValues.length > 0 ? changedValues : afterValues;
}

function buildRowResult(
  item: ActivityHistoryItem,
  attributes: AttributeSchemaBase[],
  beforeRow: ValueRow | undefined,
  afterRow: ValueRow | undefined,
  rowKey: string,
  t: TFunction,
  targetName?: string
): ResultItem | null {
  let rowOperation: "added" | "removed" | "updated";

  if (beforeRow && afterRow) {
    rowOperation = "updated";
  } else if (beforeRow && !afterRow) {
    rowOperation = "removed";
  } else {
    rowOperation = "added";
  }

  const subItems: ResultItem["subItems"] = [];
  let rowHasChanges = rowOperation === "added" || rowOperation === "removed";

  attributes.forEach((attr) => {
    const attributeId = attr.id;
    const inputType = attr?.input_type || "text";
    const attributeName = attr.display_name || attr.name || attr.id;
    const isMultiple = isMultiValue(
      inputType,
      Boolean(attr.is_multiple_choice ?? false)
    );

    const beforeValues = collectAttributeValues(
      beforeRow,
      attributeId,
      isMultiple
    );
    const rawAfterValues = collectAttributeValues(
      afterRow,
      attributeId,
      isMultiple
    );

    // Filter afterValues to only include changed/new values
    const afterValues =
      afterRow === undefined
        ? []
        : !isMultiValue
        ? getChangedAfterValues(beforeValues, rawAfterValues)
        : rawAfterValues;

    const attrOperation = determineAttributeOperation(
      beforeValues,
      afterValues,
      inputType
    );

    if (attrOperation !== "unchanged") {
      rowHasChanges = true;
    }

    let beforeValue: ActivityHistoryValue | ActivityHistoryValue[] | null =
      null;
    let afterValue: ActivityHistoryValue | ActivityHistoryValue[] | null = null;

    if (attrOperation === "added") {
      beforeValue = null;
      afterValue = isMultiple
        ? afterValues.length > 0
          ? afterValues
          : null
        : afterValues.length > 0
        ? afterValues[0]
        : null;
    } else if (attrOperation === "removed") {
      beforeValue = isMultiple
        ? beforeValues.length > 0
          ? beforeValues
          : null
        : beforeValues.length > 0
        ? beforeValues[0]
        : null;
      afterValue = null;
    } else {
      beforeValue = isMultiple
        ? beforeValues.length > 0
          ? beforeValues
          : null
        : beforeValues.length > 0
        ? beforeValues[0]
        : null;
      afterValue = isMultiple
        ? afterValues.length > 0
          ? afterValues
          : null
        : afterValues.length > 0
        ? afterValues[0]
        : null;
    }

    subItems.push({
      attributeName,
      beforeValue,
      afterValue,
      inputType,
      isMultiple,
      addressMode: inputType === "address" ? attr.address_mode : undefined,
    });
  });

  if (rowOperation === "removed") {
    const hasAnyBeforeValue = subItems.some((subItem) => {
      const beforeVal = subItem.beforeValue;
      if (beforeVal === null) return false;
      if (Array.isArray(beforeVal)) return beforeVal.length > 0;
      return true;
    });
    if (!hasAnyBeforeValue) {
      return null;
    }
  }

  if (rowOperation === "added") {
    const hasAnyAfterValue = subItems.some((subItem) => {
      const afterVal = subItem.afterValue;
      if (afterVal === null) return false;
      if (Array.isArray(afterVal)) return afterVal.length > 0;
      return true;
    });
    if (!hasAnyAfterValue) {
      return null;
    }
  }

  if (rowOperation === "updated" && !rowHasChanges) {
    return null;
  }

  return {
    id: `${item.id}-${rowKey}`,
    last_modified_by: item.last_modified_by,
    modified_at: item.modified_at,
    operation: t(mapActions(rowOperation)),
    targetName: targetName ? t(targetName) : `${item.target_type}`,
    subItems,
  };
}

function processCollectionItem(
  item: ActivityHistoryItem,
  t: TFunction
): ResultItem[] {
  const beforeRows = getRowsFromState(item.state_before);
  const afterRows = getRowsFromState(item.state_after);

  const beforeRowMap = new Map<string, ValueRow>();
  const afterRowMap = new Map<string, ValueRow>();

  beforeRows.forEach((row, index) => {
    beforeRowMap.set(buildRowKey(row, index), row);
  });

  afterRows.forEach((row, index) => {
    afterRowMap.set(buildRowKey(row, index), row);
  });

  const orderedRowKeys: string[] = [];

  afterRows.forEach((row, index) => {
    const key = buildRowKey(row, index);
    if (!orderedRowKeys.includes(key)) {
      orderedRowKeys.push(key);
    }
  });

  beforeRows.forEach((row, index) => {
    const key = buildRowKey(row, index + afterRows.length);
    if (!orderedRowKeys.includes(key)) {
      orderedRowKeys.push(key);
    }
  });

  if (orderedRowKeys.length === 0) {
    return [];
  }

  const attributes = collectAttributes(item.state_before, item.state_after);
  const rowResults: ResultItem[] = [];

  orderedRowKeys.forEach((rowKey, _) => {
    const rowResult = buildRowResult(
      item,
      attributes,
      beforeRowMap.get(rowKey),
      afterRowMap.get(rowKey),
      rowKey,
      t
    );

    if (rowResult) {
      rowResults.push(rowResult);
    }
  });

  return rowResults;
}

function processPricingItem(
  item: ActivityHistoryItem,
  t: TFunction
): ResultItem[] {
  const beforeRows = getPricingRowsFromState(item.state_before);
  const afterRows = getPricingRowsFromState(item.state_after);

  const beforeRowMap = new Map<string, ValueRow>();
  const afterRowMap = new Map<string, ValueRow>();

  beforeRows.forEach((row, index) => {
    beforeRowMap.set(buildRowKey(row, index), row);
  });

  afterRows.forEach((row, index) => {
    afterRowMap.set(buildRowKey(row, index), row);
  });

  const orderedRowKeys: string[] = [];

  afterRows.forEach((row, index) => {
    const key = buildRowKey(row, index);
    if (!orderedRowKeys.includes(key)) {
      orderedRowKeys.push(key);
    }
  });

  beforeRows.forEach((row, index) => {
    const key = buildRowKey(row, index + afterRows.length);
    if (!orderedRowKeys.includes(key)) {
      orderedRowKeys.push(key);
    }
  });

  if (orderedRowKeys.length === 0) {
    return [];
  }

  // Use pricing attributes instead of collecting from state
  const attributes = createPricingAttributes(t);
  const rowResults: ResultItem[] = [];

  orderedRowKeys.forEach((rowKey, _) => {
    const rowResult = buildRowResult(
      item,
      attributes,
      beforeRowMap.get(rowKey),
      afterRowMap.get(rowKey),
      rowKey,
      t,
      "List Prices"
    );

    if (rowResult) {
      rowResults.push(rowResult);
    }
  });

  return rowResults;
}

export function parseCollections(
  items: ActivityHistoryItem[],
  t: TFunction
): ResultItem[] {
  const results: ResultItem[] = [];

  items.forEach((item) => {
    if (hasCollectionData(item)) {
      results.push(...processCollectionItem(item, t));
    } else if (hasPricingData(item)) {
      results.push(...processPricingItem(item, t));
    }
  });

  return results;
}
